/**
 * Breadcrumb registry — maps a route template to an i18n label.
 *
 * Powers the ⌘K command palette's route index. The legacy `<Breadcrumbs />`
 * strip rendered inside AppLayout/CompanyLayout was removed 2026-04-22 once
 * each overview hero gained its own integrated breadcrumb trail, so the
 * runtime `resolveBreadcrumbs()` helper is no longer needed — only the
 * registry data survives here.
 *
 * - Literal paths use exact strings (e.g. `/location/bookings/calendar`).
 * - Dynamic segments use `:param` placeholders (e.g. `/location/bookings/:id`).
 *
 * Labels are i18n keys (with a safe English fallback). The optional `dynamic`
 * function lets a segment render a live value such as an entity name.
 */

export interface BreadcrumbRegistryEntry {
  /** i18n key used to look up the label. */
  labelKey: string;
  /** English fallback shown when the i18n key is missing. */
  fallback: string;
  /**
   * Optional formatter that receives the current route params and returns a
   * string to render instead of the i18n label (e.g. "Booking #ABC-123").
   */
  dynamic?: (params: Record<string, string | undefined>) => string | null;
}

export const breadcrumbRegistry: Record<string, BreadcrumbRegistryEntry> = {
  // Top-level dashboards
  // /management/overview redirects to /company/overview as of 2026-04-25;
  // the company-scope overview's breadcrumb is owned by CompanyLayout, not here.
  '/location/frontdesk/overview': { labelKey: 'breadcrumbs.frontdesk.overview', fallback: 'Frontdesk' },
  '/location/products-sales/overview': { labelKey: 'breadcrumbs.productsSales.overview', fallback: 'Products & Sales' },
  '/location/hr/overview': { labelKey: 'breadcrumbs.hr.overview', fallback: 'Human Resources' },
  '/location/treasury/overview': { labelKey: 'breadcrumbs.treasury.overview', fallback: 'Treasury' },
  '/location/invoices': { labelKey: 'platformInvoices.title', fallback: 'Platform invoices' },

  // Bookings
  '/location/bookings': { labelKey: 'breadcrumbs.bookings.list', fallback: 'Bookings' },
  '/location/bookings/new': { labelKey: 'breadcrumbs.bookings.new', fallback: 'New booking' },
  '/location/bookings/checkin': { labelKey: 'breadcrumbs.bookings.checkin', fallback: 'Check-in' },
  '/location/bookings/calendar': { labelKey: 'breadcrumbs.bookings.calendar', fallback: 'Calendar' },
  '/location/bookings/:id': {
    labelKey: 'breadcrumbs.bookings.detail',
    fallback: 'Booking',
    dynamic: (params) => (params.id ? `#${params.id.slice(0, 8)}` : null),
  },
  '/location/bookings/:id/statement': { labelKey: 'breadcrumbs.bookings.statement', fallback: 'Statement' },

  // Guests / persons
  '/location/guests': { labelKey: 'breadcrumbs.guests.list', fallback: 'Guests' },
  '/location/guests/offerings': { labelKey: 'breadcrumbs.guests.offerings', fallback: 'Offerings' },
  '/location/guests/:guestId/statement': { labelKey: 'breadcrumbs.guests.statement', fallback: 'Guest statement' },
  '/persons': { labelKey: 'breadcrumbs.persons.list', fallback: 'Directory' },
  '/persons/:personId': { labelKey: 'breadcrumbs.persons.detail', fallback: 'Person' },
  '/profile': { labelKey: 'breadcrumbs.profile', fallback: 'My profile' },

  // Rooms
  '/location/rooms': { labelKey: 'breadcrumbs.rooms.list', fallback: 'Rooms' },

  // Activities — calendar-first hub (V2). Scheduling and template
  // create/edit are drawers, not routes, so they have no breadcrumb entry.
  '/location/activities': { labelKey: 'breadcrumbs.activities.list', fallback: 'Activities' },

  // Bar & Kitchen
  '/location/bar-kitchen/menus': { labelKey: 'breadcrumbs.barKitchen.menus', fallback: 'Menus' },
  '/location/bar-kitchen/recipes': { labelKey: 'breadcrumbs.barKitchen.recipes', fallback: 'Recipes' },
  '/location/bar-kitchen/products': { labelKey: 'breadcrumbs.barKitchen.products', fallback: 'Products' },
  '/location/bar-kitchen/stock': { labelKey: 'breadcrumbs.barKitchen.stock', fallback: 'Stock' },

  // Shop
  '/location/shop/catalog': { labelKey: 'breadcrumbs.shop.catalog', fallback: 'Catalog' },
  '/location/shop/products': { labelKey: 'breadcrumbs.shop.products', fallback: 'Products' },
  '/location/shop/stock': { labelKey: 'breadcrumbs.shop.stock', fallback: 'Stock' },

  // POS & Sales
  '/location/pos/overview': { labelKey: 'breadcrumbs.pos.overview', fallback: 'POS' },
  '/location/pos/terminals': { labelKey: 'breadcrumbs.pos.terminals', fallback: 'Terminals' },
  '/location/pos/close': { labelKey: 'breadcrumbs.pos.close', fallback: 'Daily close' },
  '/location/sales': { labelKey: 'breadcrumbs.sales.history', fallback: 'Sales history' },
  '/location/sales/reports': { labelKey: 'breadcrumbs.sales.reports', fallback: 'Sales reports' },

  // HR
  '/location/staff': { labelKey: 'breadcrumbs.staff.list', fallback: 'Staff' },
  '/location/staff/positions': { labelKey: 'breadcrumbs.staff.positions', fallback: 'Positions' },
  '/location/staff/tokens': { labelKey: 'breadcrumbs.staff.tokens', fallback: 'Tokens' },
  '/location/shifts': { labelKey: 'breadcrumbs.shifts.list', fallback: 'Shifts' },
  '/location/payroll': { labelKey: 'breadcrumbs.payroll.list', fallback: 'Payroll' },

  // Treasury
  // R-FE-1.8: /location/treasury/banking is tombstoned, but keep a
  // crumb so any externally-cached link still renders something
  // sensible while the redirect runs.
  '/location/treasury/banking': { labelKey: 'breadcrumbs.treasury.setup', fallback: 'Treasury setup' },
  '/location/treasury/operations': { labelKey: 'breadcrumbs.treasury.operations', fallback: 'Operations' },
  '/location/treasury/pending': { labelKey: 'breadcrumbs.treasury.pending', fallback: 'Pending' },
  '/location/treasury/cash': { labelKey: 'breadcrumbs.treasury.cash', fallback: 'Cash' },
  '/location/treasury/reconciliation': { labelKey: 'breadcrumbs.treasury.reconciliation', fallback: 'Reconciliation' },
  '/location/treasury/reports': { labelKey: 'breadcrumbs.treasury.reports', fallback: 'Reports' },

  // Suppliers
  '/location/suppliers': { labelKey: 'breadcrumbs.suppliers.list', fallback: 'Suppliers' },

  // Inventory

  // Locations
  '/locations': { labelKey: 'breadcrumbs.locations.list', fallback: 'Locations' },

  // Settings
  '/location/settings': { labelKey: 'breadcrumbs.settings.index', fallback: 'Settings' },
  '/location/settings/general': { labelKey: 'breadcrumbs.settings.general', fallback: 'General' },
  '/location/settings/seasons': { labelKey: 'breadcrumbs.settings.seasons', fallback: 'Seasons' },
  '/location/settings/staff': { labelKey: 'breadcrumbs.settings.staff', fallback: 'Staff' },
  '/location/settings/billing': { labelKey: 'breadcrumbs.settings.billing', fallback: 'Billing' },
  '/location/settings/contact': { labelKey: 'breadcrumbs.settings.contact', fallback: 'Contact' },
  // R-FE-1.8: /location/settings/banks redirects to the treasury hub.
  '/location/settings/banks': { labelKey: 'breadcrumbs.treasury.setup', fallback: 'Treasury setup' },
  '/location/settings/treasury': { labelKey: 'breadcrumbs.treasury.setup', fallback: 'Treasury setup' },
  '/location/settings/integrations': { labelKey: 'breadcrumbs.settings.integrations', fallback: 'Integrations' },
  '/settings/notifications': { labelKey: 'breadcrumbs.settings.notifications', fallback: 'Notifications' },

  // Notifications — one personal centre, plus the two shared queues.
  '/notifications': { labelKey: 'breadcrumbs.notifications', fallback: 'Notifications' },
  '/location/:locationId/notifications': { labelKey: 'breadcrumbs.notifications', fallback: 'Notifications' },
  '/company/notifications': { labelKey: 'breadcrumbs.notifications', fallback: 'Notifications' },

  // Company scope
  // /company/overview is owned by CompanyLayout (not registered here);
  // /company/dashboard was retired 2026-04-28 — call sites point to
  // /company/overview directly.
  '/company/locations': { labelKey: 'breadcrumbs.company.locations', fallback: 'Locations' },
  '/company/locations/overview': { labelKey: 'breadcrumbs.company.locationsOverview', fallback: 'Locations overview' },
  '/company/locations/:id': { labelKey: 'breadcrumbs.company.location', fallback: 'Location' },
  '/company/locations/:id/settings': { labelKey: 'breadcrumbs.company.locationSettings', fallback: 'Settings' },
  '/company/treasury': { labelKey: 'breadcrumbs.company.treasury', fallback: 'Treasury' },
  '/company/invoices': { labelKey: 'platformInvoices.title', fallback: 'Platform invoices' },
  '/company/banks': { labelKey: 'breadcrumbs.company.banks', fallback: 'Banks' },
  '/company/users': { labelKey: 'breadcrumbs.company.users', fallback: 'Users & roles' },
  '/company/users/:id': { labelKey: 'breadcrumbs.company.user', fallback: 'User' },
  '/company/staff': { labelKey: 'breadcrumbs.company.staff', fallback: 'Staff' },
  '/company/staff/positions': { labelKey: 'breadcrumbs.company.positions', fallback: 'Positions' },
  '/company/payroll': { labelKey: 'breadcrumbs.company.payroll', fallback: 'Payroll' },
  '/company/tokens': { labelKey: 'breadcrumbs.company.tokens', fallback: 'Tokens' },
  '/company/settings': { labelKey: 'breadcrumbs.company.settings', fallback: 'Settings' },
  '/company/settings/integrations': { labelKey: 'breadcrumbs.company.integrations', fallback: 'Integrations' },
};
